import { useState } from "react";
import { api } from "../api";
import { Modal } from "./Modal";
import type { CrmCatalog, LeadView } from "../types";

/**
 * Ручная правка карточки. Всё, что здесь меняют, помечается как «правлено
 * руками» — повторный разбор записи такие поля больше не переписывает.
 */
export function LeadEditModal({
  lead,
  catalog,
  onClose,
  onSaved,
}: {
  lead: LeadView;
  catalog: CrmCatalog | null;
  onClose: () => void;
  onSaved: (lead: LeadView) => void;
}) {
  const [form, setForm] = useState({
    company: lead.company,
    contact_name: lead.contact_name,
    contact_role: lead.contact_role,
    contacts: lead.contacts,
    city: lead.city,
    client_type: lead.client_type,
    category: lead.category,
    status: lead.status,
    temperature: lead.temperature,
    interests: lead.interests,
    event_name: lead.event_name,
    follow_up_at: lead.follow_up_at ? lead.follow_up_at.slice(0, 10) : "",
    needs: lead.needs,
    budget: lead.budget,
    timeline: lead.timeline,
    notes: lead.notes,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const toggleInterest = (key: string) =>
    set(
      "interests",
      form.interests.includes(key)
        ? form.interests.filter((i) => i !== key)
        : [...form.interests, key],
    );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onSaved(
        await api.updateLead(lead.meeting_id, {
          ...form,
          follow_up_at: form.follow_up_at || null,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Карточка клиента"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Отмена</button>
          <button className="primary" form="lead-form" type="submit" disabled={busy}>
            {busy && <span className="spinner" />} Сохранить
          </button>
        </>
      }
    >
      <form id="lead-form" onSubmit={submit}>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="l-company">Компания</label>
            <input
              id="l-company"
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="l-city">Город</label>
            <input
              id="l-city"
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
            />
          </div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="l-name">Контакт</label>
            <input
              id="l-name"
              value={form.contact_name}
              onChange={(e) => set("contact_name", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="l-role">Должность</label>
            <input
              id="l-role"
              value={form.contact_role}
              onChange={(e) => set("contact_role", e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="l-contacts">Телефон, почта</label>
          <input
            id="l-contacts"
            value={form.contacts}
            onChange={(e) => set("contacts", e.target.value)}
          />
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="l-type">Тип клиента</label>
            <select
              id="l-type"
              value={form.client_type}
              onChange={(e) => set("client_type", e.target.value)}
            >
              <option value="">не указан</option>
              {catalog?.client_types.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="l-cat">Категория</label>
            <select
              id="l-cat"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
            >
              <option value="">не указана</option>
              {catalog?.categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="l-temp">Интерес</label>
            <select
              id="l-temp"
              value={form.temperature}
              onChange={(e) =>
                set("temperature", e.target.value as LeadView["temperature"])
              }
            >
              <option value="hot">🔥 горячий</option>
              <option value="warm">🙂 тёплый</option>
              <option value="cold">❄️ холодный</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>Направления</label>
          <div className="row">
            {catalog?.interests.map((i) => {
              const on = form.interests.includes(i.key);
              return (
                <button
                  key={i.key}
                  type="button"
                  className={on ? "sm primary" : "sm"}
                  onClick={() => toggleInterest(i.key)}
                >
                  {on ? "✓ " : ""}
                  {i.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="l-status">Статус</label>
            <select
              id="l-status"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
            >
              {catalog?.statuses.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="l-follow">Напомнить</label>
            <input
              id="l-follow"
              type="date"
              value={form.follow_up_at}
              onChange={(e) => set("follow_up_at", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="l-event">Выставка</label>
            <input
              id="l-event"
              value={form.event_name}
              onChange={(e) => set("event_name", e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="l-needs">Что нужно клиенту</label>
          <textarea
            id="l-needs"
            value={form.needs}
            onChange={(e) => set("needs", e.target.value)}
          />
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="l-budget">Бюджет</label>
            <input
              id="l-budget"
              value={form.budget}
              onChange={(e) => set("budget", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="l-timeline">Сроки</label>
            <input
              id="l-timeline"
              value={form.timeline}
              onChange={(e) => set("timeline", e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="l-notes">Заметки менеджера</label>
          <textarea
            id="l-notes"
            value={form.notes}
            placeholder="То, чего не было в разговоре: впечатление, договорённости в кулуарах"
            onChange={(e) => set("notes", e.target.value)}
          />
        </div>

        <div className="muted small">
          Изменённые поля закрепляются за вами: если запись разберут заново, они
          останутся как есть.
        </div>

        {error && <div className="error-text">{error}</div>}
      </form>
    </Modal>
  );
}
